/**
 * Trainer Meeting Tools — Polls: create page + completed poll list.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, ChevronLeft, Plus, Trash2 } from "lucide-react";
import {
  createPoll,
  endPoll,
  fetchActivePoll,
  fetchPollHistory,
} from "./liveCollaborationApi";
import type { LivePoll, PollOption, PollVote } from "./liveCollaborationTypes";
import { newCollabId } from "./liveCollaborationTypes";

const FIXED_POLL_DURATION_SECONDS = 30;
const MIN_CUSTOM_POLL_SECONDS = 10;
const MAX_CUSTOM_POLL_SECONDS = 600;

const fieldStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "#1a1d26",
  color: "#e2e8f0",
  padding: "10px 12px",
  fontSize: 13,
  fontFamily: "inherit",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: 6,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#64748b",
};

type Mode = "list" | "create" | "results";

function isPollEndNotFound(err: unknown): boolean {
  const anyErr = err as {
    response?: { status?: number; data?: { detail?: string } };
  };
  if (anyErr.response?.status === 404) return true;
  const detail = String(anyErr.response?.data?.detail || "").toLowerCase();
  return detail.includes("not found");
}

type Props = {
  sessionId: string;
  trainerId: string;
  trainerName: string;
  poll: LivePoll | null;
  /** Wait for room bootstrap attempt before active-poll REST. */
  hydrateReady?: boolean;
  /** Room collab/bootstrap succeeded — skip active-poll REST. */
  skipHydrate?: boolean;
  onPoll: (poll: LivePoll | null) => void;
  onPublish: (
    poll: LivePoll,
    opts?: { broadcast?: boolean },
  ) => Promise<void>;
};

function formatEndedAt(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function votersForOption(poll: LivePoll, optionId: string): PollVote[] {
  return (poll.votes || []).filter((v) => v.option_id === optionId);
}

function PollOptionBar({
  option,
  total,
  selected,
  voters,
  onSelect,
}: {
  option: PollOption;
  total: number;
  selected: boolean;
  voters: PollVote[];
  onSelect: () => void;
}) {
  const pct = total > 0 ? Math.round((option.votes / total) * 100) : 0;
  return (
    <div
      style={{
        borderRadius: 12,
        border: selected
          ? "1px solid rgba(59,130,246,0.5)"
          : "1px solid rgba(255,255,255,0.1)",
        background: selected ? "rgba(59,130,246,0.12)" : "#1a1d26",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onSelect}
        style={{
          width: "100%",
          border: "none",
          background: "transparent",
          padding: 12,
          textAlign: "left",
          cursor: "pointer",
          color: "inherit",
          font: "inherit",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13,
          }}
        >
          <span style={{ color: "#fff" }}>{option.text}</span>
          <span style={{ color: "#94a3b8" }}>
            {pct}% · {option.votes} student{option.votes === 1 ? "" : "s"}
          </span>
        </div>
        <div
          style={{
            marginTop: 8,
            height: 6,
            overflow: "hidden",
            borderRadius: 999,
            background: "rgba(255,255,255,0.1)",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "#3b82f6",
              borderRadius: 999,
            }}
          />
        </div>
      </button>
      {selected ? (
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            padding: "10px 12px 12px",
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#64748b",
            }}
          >
            Students who selected “{option.text}”
          </p>
          {voters.length === 0 ? (
            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
              No students selected this option.
            </p>
          ) : (
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {voters.map((v) => (
                <li
                  key={`${v.student_id}-${v.option_id}`}
                  style={{
                    borderRadius: 8,
                    background: "rgba(0,0,0,0.25)",
                    padding: "8px 10px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#e2e8f0",
                  }}
                >
                  {v.student_name?.trim() || v.student_id || "Student"}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PollResultsView({
  poll,
  remaining,
  showHeader = true,
}: {
  poll: LivePoll;
  remaining?: string;
  showHeader?: boolean;
}) {
  const total = poll.total_votes || 0;
  const missed = poll.missed_count || 0;
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {showHeader ? (
        <>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 700,
              color: "#fff",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {poll.question}
          </p>
          <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
            {total} vote{total === 1 ? "" : "s"}
            {missed > 0 ? ` · ${missed} missed` : ""}
            {poll.status === "active" && remaining
              ? ` · Ends in ${remaining}`
              : poll.status === "ended"
                ? " · Ended"
                : ""}
            {" · Tap an option to see students"}
          </p>
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
          Tap an option to see students
        </p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {poll.options.map((o) => (
          <PollOptionBar
            key={o.id}
            option={o}
            total={total}
            selected={selectedOptionId === o.id}
            voters={votersForOption(poll, o.id)}
            onSelect={() =>
              setSelectedOptionId((prev) => (prev === o.id ? null : o.id))
            }
          />
        ))}
      </div>
    </div>
  );
}

function CompletedPollsList({
  polls,
  expandedId,
  onToggle,
}: {
  polls: LivePoll[];
  expandedId: string | null;
  onToggle: (pollId: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={labelStyle}>All polls</p>
      {polls.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
          No polls yet. Launch one to see results here.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {polls.map((p) => {
            const open = expandedId === p.poll_id;
            const votes = p.total_votes || 0;
            const missed = p.missed_count || 0;
            const isActive = p.status === "active";
            return (
              <div
                key={p.poll_id}
                style={{
                  borderRadius: 12,
                  border: isActive
                    ? "1px solid rgba(56,189,248,0.45)"
                    : "1px solid rgba(255,255,255,0.1)",
                  background: "#1a1d26",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => onToggle(p.poll_id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    background: "transparent",
                    padding: 12,
                    cursor: "pointer",
                    color: "inherit",
                    font: "inherit",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#fff",
                        lineHeight: 1.35,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {p.question}
                    </p>
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: isActive ? "#7dd3fc" : "#94a3b8",
                      }}
                    >
                      {isActive ? "Active" : "Ended"}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: 11,
                      color: "#64748b",
                    }}
                  >
                    {votes} vote{votes === 1 ? "" : "s"}
                    {missed > 0 ? ` · ${missed} missed` : ""}
                    {p.created_at
                      ? ` · ${formatEndedAt(p.created_at)}`
                      : ""}
                  </p>
                </button>
                {open ? (
                  <div style={{ padding: "0 12px 12px" }}>
                    <PollResultsView poll={p} showHeader={false} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CreatePollForm({
  busy,
  error,
  onLaunch,
  onBack,
}: {
  busy: boolean;
  error: string;
  onLaunch: (params: {
    question: string;
    options: string[];
    durationSeconds: number;
  }) => void;
  onBack: () => void;
}) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [localError, setLocalError] = useState("");
  const [durationMode, setDurationMode] = useState<"fixed" | "custom">("fixed");
  const [customSeconds, setCustomSeconds] = useState("60");

  const addOption = () => {
    if (options.length >= 6) return;
    setOptions((prev) => [...prev, ""]);
  };

  const removeOption = (index: number) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    setLocalError("");
    setOptions((prev) => prev.map((o, i) => (i === index ? value : o)));
  };

  const duplicateIndexes = useMemo(() => {
    const counts = new Map<string, number>();
    options.forEach((o) => {
      const key = o.trim().toLowerCase();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const dupes = new Set<number>();
    options.forEach((o, i) => {
      const key = o.trim().toLowerCase();
      if (key && (counts.get(key) || 0) > 1) dupes.add(i);
    });
    return dupes;
  }, [options]);

  const launch = () => {
    const trimmedQuestion = question.trim();
    const labels = options.map((o) => o.trim()).filter(Boolean);
    if (!trimmedQuestion) {
      setLocalError("Enter a poll question.");
      return;
    }
    if (labels.length < 2) {
      setLocalError("Add at least two answer options.");
      return;
    }
    if (duplicateIndexes.size > 0) {
      setLocalError("Poll options must be unique. No two options can be the same.");
      return;
    }
    let durationSeconds = FIXED_POLL_DURATION_SECONDS;
    if (durationMode === "custom") {
      const parsed = Number(customSeconds);
      if (!Number.isFinite(parsed)) {
        setLocalError("Enter a custom duration in seconds.");
        return;
      }
      durationSeconds = Math.round(parsed);
      if (
        durationSeconds < MIN_CUSTOM_POLL_SECONDS ||
        durationSeconds > MAX_CUSTOM_POLL_SECONDS
      ) {
        setLocalError(
          `Custom time must be ${MIN_CUSTOM_POLL_SECONDS}–${MAX_CUSTOM_POLL_SECONDS} seconds.`,
        );
        return;
      }
    }
    setLocalError("");
    onLaunch({
      question: trimmedQuestion,
      options: labels,
      durationSeconds,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          border: "none",
          background: "transparent",
          color: "#94a3b8",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          padding: 0,
        }}
      >
        <ChevronLeft size={16} />
        Back to polls
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "#94a3b8",
        }}
      >
        <BarChart3 size={16} />
        Create a live poll for students
      </div>

      <div>
        <label style={labelStyle}>Question</label>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. How clear was today's topic?"
          style={fieldStyle}
        />
      </div>

      <div>
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <label style={{ ...labelStyle, marginBottom: 0 }}>Options</label>
          <button
            type="button"
            onClick={addOption}
            disabled={options.length >= 6}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              border: "none",
              background: "transparent",
              color: options.length >= 6 ? "#475569" : "#60a5fa",
              fontSize: 12,
              fontWeight: 700,
              cursor: options.length >= 6 ? "not-allowed" : "pointer",
              padding: 0,
            }}
          >
            <Plus size={14} />
            Add option
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {options.map((opt, index) => (
            <div
              key={index}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <input
                type="text"
                value={opt}
                onChange={(e) => updateOption(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
                style={{
                  ...fieldStyle,
                  flex: 1,
                  minWidth: 0,
                  border: duplicateIndexes.has(index)
                    ? "1px solid #f87171"
                    : fieldStyle.border,
                }}
              />
              <button
                type="button"
                onClick={() => removeOption(index)}
                disabled={options.length <= 2}
                aria-label="Remove option"
                style={{
                  border: "none",
                  borderRadius: 8,
                  padding: 8,
                  background: "rgba(255,255,255,0.06)",
                  color: options.length <= 2 ? "#475569" : "#94a3b8",
                  cursor: options.length <= 2 ? "not-allowed" : "pointer",
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>Duration</label>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => setDurationMode("fixed")}
            style={{
              flex: 1,
              border:
                durationMode === "fixed"
                  ? "1px solid rgba(59,130,246,0.55)"
                  : "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12,
              padding: "10px 12px",
              background:
                durationMode === "fixed" ? "rgba(37,99,235,0.18)" : "#1a1d26",
              color: "#e2e8f0",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {FIXED_POLL_DURATION_SECONDS} seconds
          </button>
          <button
            type="button"
            onClick={() => setDurationMode("custom")}
            style={{
              flex: 1,
              border:
                durationMode === "custom"
                  ? "1px solid rgba(59,130,246,0.55)"
                  : "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12,
              padding: "10px 12px",
              background:
                durationMode === "custom" ? "rgba(37,99,235,0.18)" : "#1a1d26",
              color: "#e2e8f0",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Custom time
          </button>
        </div>
        {durationMode === "custom" ? (
          <div style={{ marginTop: 8 }}>
            <input
              type="number"
              min={MIN_CUSTOM_POLL_SECONDS}
              max={MAX_CUSTOM_POLL_SECONDS}
              step={5}
              value={customSeconds}
              onChange={(e) => setCustomSeconds(e.target.value)}
              placeholder="Seconds"
              style={fieldStyle}
            />
            <p style={{ margin: "6px 0 0", fontSize: 11, color: "#64748b" }}>
              {MIN_CUSTOM_POLL_SECONDS}–{MAX_CUSTOM_POLL_SECONDS} seconds
            </p>
          </div>
        ) : null}
      </div>

      {localError || error ? (
        <p style={{ margin: 0, fontSize: 12, color: "#f87171" }}>
          {localError || error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={launch}
        style={{
          width: "100%",
          border: "none",
          borderRadius: 12,
          padding: "12px 14px",
          background: busy ? "#475569" : "#2563eb",
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "Launching…" : "Launch poll"}
      </button>
    </div>
  );
}

export function TrainerPollsPanel({
  sessionId,
  trainerId,
  trainerName,
  poll,
  hydrateReady = true,
  skipHydrate = false,
  onPoll,
  onPublish,
}: Props) {
  const [mode, setMode] = useState<Mode>(
    poll?.status === "active" ? "results" : "list",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [history, setHistory] = useState<LivePoll[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const refreshHistory = useCallback(async () => {
    try {
      const rows = await fetchPollHistory(sessionId);
      setHistory(rows);
    } catch (err) {
      console.error("fetchPollHistory failed", err);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!hydrateReady) return;
    let cancelled = false;
    void (async () => {
      if (!skipHydrate) {
        try {
          const active = await fetchActivePoll(sessionId);
          if (cancelled) return;
          onPoll(active);
          if (active?.status === "active") setMode("results");
        } catch (err) {
          console.error("fetchActivePoll failed", err);
        }
      } else if (poll?.status === "active") {
        setMode("results");
      }
      if (!cancelled) await refreshHistory();
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, hydrateReady, skipHydrate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (mode !== "results" || poll?.status !== "active") return;
    const id = window.setInterval(() => {
      void (async () => {
        try {
          const active = await fetchActivePoll(sessionId);
          if (active) onPoll(active);
        } catch {
          /* ignore */
        }
      })();
    }, 15000);
    return () => window.clearInterval(id);
  }, [mode, poll?.status, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (poll?.status === "active") setMode("results");
  }, [poll?.poll_id, poll?.status]);

  const pollAutoEndAttemptedRef = useRef<Set<string>>(new Set());

  const remaining = useMemo(() => {
    if (!poll?.ends_at || poll.status !== "active") return "";
    const sec = Math.max(0, Math.floor((poll.ends_at - now) / 1000));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  }, [poll, now]);

  // All polls, newest first (API already sorts by created_at DESC).
  const listedPolls = useMemo(() => {
    const byId = new Map<string, LivePoll>();
    for (const p of history) {
      if (p?.poll_id) byId.set(p.poll_id, p);
    }
    // Prefer live active poll payload when present (fresher vote tallies).
    if (poll?.poll_id) {
      const existing = byId.get(poll.poll_id);
      byId.set(poll.poll_id, existing ? { ...existing, ...poll } : poll);
    }
    const ts = (p: LivePoll) =>
      Date.parse(String(p.created_at || "")) || Number(p.ends_at || 0) || 0;
    return Array.from(byId.values()).sort((a, b) => ts(b) - ts(a));
  }, [history, poll]);

  const startPoll = async (params: {
    question: string;
    options: string[];
    durationSeconds: number;
  }) => {
    if (busy) return;
    setBusy(true);
    setError("");
    const pollId = newCollabId("poll");
    const durationSec = params.durationSeconds;
    const options: PollOption[] = params.options.map((text, i) => ({
      id: `opt-${i + 1}`,
      text,
      votes: 0,
    }));
    const optimistic: LivePoll = {
      poll_id: pollId,
      session_id: sessionId,
      question: params.question,
      options,
      status: "active",
      total_votes: 0,
      missed_count: 0,
      ends_at: Date.now() + durationSec * 1000,
      created_by: {
        identity: trainerId,
        role: "trainer",
        display_name: trainerName,
      },
      created_at: new Date().toISOString(),
      votes: [],
    };
    try {
      // Instant fan-out — students see the poll before REST returns.
      await onPublish(optimistic);
      setMode("results");
      const created = await createPoll({
        sessionId,
        trainerId,
        trainerName,
        question: params.question,
        options: params.options,
        durationSeconds: durationSec,
        pollId,
      });
      if (created.poll_id !== pollId || created.ends_at !== optimistic.ends_at) {
        await onPublish(created);
      }
      await refreshHistory();
    } catch (err) {
      console.error(err);
      setError("Could not launch poll. Try again.");
      // Retract optimistic LiveKit poll so students don't vote a ghost id.
      try {
        await onPublish({
          ...optimistic,
          status: "ended",
          ends_at: Date.now(),
        });
      } catch {
        /* ignore */
      }
      onPoll(null);
      setMode("list");
    } finally {
      setBusy(false);
    }
  };

  const stopPoll = async () => {
    if (!poll || busy) return;
    setBusy(true);
    const localPoll = poll;
    const asEnded = (p: LivePoll): LivePoll => ({
      ...p,
      status: "ended",
      ends_at: Date.now(),
    });
    try {
      let targetPoll = localPoll;
      try {
        const active = await fetchActivePoll(sessionId);
        if (!active || active.status === "ended") {
          onPoll(null);
          setMode("list");
          await onPublish(asEnded(localPoll));
          await refreshHistory();
          return;
        }
        targetPoll = active;
      } catch (err) {
        console.error("fetchActivePoll before end failed", err);
      }

      onPoll(null);
      setMode("list");

      let ended: LivePoll | null = null;
      try {
        ended = await endPoll({ sessionId, pollId: targetPoll.poll_id });
      } catch (err) {
        if (!isPollEndNotFound(err)) throw err;
      }

      if (ended) {
        await onPublish(ended);
      } else {
        await onPublish(asEnded(targetPoll));
      }
      await refreshHistory();
    } catch (err) {
      console.error(err);
      onPoll(null);
      setMode("list");
      try {
        await onPublish(asEnded(localPoll));
      } catch {
        /* ignore */
      }
      await refreshHistory();
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!poll || poll.status !== "active" || !poll.ends_at) return;
    if (poll.ends_at > now) return;
    if (pollAutoEndAttemptedRef.current.has(poll.poll_id)) return;
    pollAutoEndAttemptedRef.current.add(poll.poll_id);
    void stopPoll();
  }, [poll?.poll_id, poll?.status, poll?.ends_at, now]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedSection = (
    <CompletedPollsList
      polls={listedPolls}
      expandedId={expandedId}
      onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
    />
  );

  if (mode === "results" && poll && poll.status === "active") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <PollResultsView poll={poll} remaining={remaining} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            type="button"
            disabled={busy}
            onClick={() => void stopPoll()}
            style={{
              width: "100%",
              borderRadius: 12,
              border: "1px solid rgba(244,63,94,0.4)",
              background: "rgba(244,63,94,0.1)",
              color: "#fda4af",
              padding: "10px 12px",
              fontSize: 12,
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            End poll
          </button>
          <button
            type="button"
            onClick={() => setMode("create")}
            style={{
              width: "100%",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "transparent",
              color: "#94a3b8",
              padding: "10px 12px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Create new poll
          </button>
        </div>
        {completedSection}
      </div>
    );
  }

  if (mode === "create") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <CreatePollForm
          busy={busy}
          error={error}
          onBack={() => setMode("list")}
          onLaunch={(p) => void startPoll(p)}
        />
        {completedSection}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "#94a3b8",
        }}
      >
        <BarChart3 size={16} />
        Launch polls and review completed results
      </div>

      <button
        type="button"
        onClick={() => {
          setError("");
          setMode("create");
        }}
        style={{
          width: "100%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          border: "none",
          borderRadius: 12,
          padding: "12px 14px",
          background: "#2563eb",
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          cursor: "pointer",
        }}
      >
        <Plus size={16} />
        Create poll
      </button>

      {completedSection}
    </div>
  );
}
